import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { task } from 'ember-concurrency';

/**
 * Get the count meta from the raw JSON response if there is one.
 *
 * @param {Object} response
 * @return {Object|null}
 */
const getCountMetaFromResponse = (response) => {
  if (!response.meta) {
    return null;
  }

  const { meta } = response;
  return {
    finalNotOverdueCount: meta.final_not_overdue_count,
    finalOverdueCount: meta.final_overdue_count,
    finalPaidCount: meta.final_paid_count,
  }
};

/**
 * Service for sh-invoice related API calls
 */
export default Service.extend({
  api: service(),
  appCache: service(),
  shData: service(),
  store: service(),

  /**
   * Approve an advance
   *
   * @param {ShInvoice} shInvoice
   */
  agreeAdvance: task(function *(shInvoice) {
    yield this.get('api').post(`sh-invoices/${shInvoice.get('id')}/agree`);
  }),

  /**
   * Add a list of invoice ids to a batched invoice.
   *
   * @param {ShInvoice} batchedInvoice
   * @param {String[]} invoiceIdsToAdd
   * @return {Ember.RSVP.Promise}
   */
  addInvoicesToBatch(batchedInvoice, invoiceIdsToAdd) {
    return new Promise((resolve, reject) => {
      const options = {
        jsonData: {
          'sh-invoice-ids' : invoiceIdsToAdd.join(',')
        }
      };

      this.shData.addIncludesToOptions(options, []);

      this.get('api').put(`sh-invoices/${batchedInvoice.get('id')}/`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error adding invoices to the batch.  Please refresh and try again.',
          'addInvoicesToBatch -- error adding invoices to batch : ' + batchedInvoice.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Add or update a memo to the given invoice.
   *
   * @param {ShInvoice} shInvoice
   * @param {String} memo
   * @param {String[]=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  addMemoToInvoice(shInvoice, memo, opt_includes = []) {
    return new Promise((resolve, reject) => {
      const options = {
        jsonData: {
          'memo' : memo
        }
      };

      this.shData.addIncludesToOptions(options, opt_includes);

      this.get('api').post(`sh-invoices/${shInvoice.get('id')}/memo`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let shInvoiceFromDB = this.get('store').peekRecord('shInvoice', response.data.id);
        resolve(shInvoiceFromDB);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error adding memo to invoice.  Please refresh and try again.',
          'addMemoToInvoice -- error adding memo to invoice : ' + shInvoice.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Approve and reload an Invoice
   *
   * @param {String} shInvoice
   * @param {String[]} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  approveInvoice(shInvoice, opt_includes = []) {
    const options = {};
    this.shData.addIncludesToOptions(options, []);

      return this.get('api').post(`sh-invoices/${shInvoice.get('id')}/approve`, options).then((response) => {
        this.shData.pushResponseToStore(response);
        return this.get('store').peekRecord('shInvoice', response.data.id);
      });
  },

  /**
   * Decline an invoice awaiting approval
   *
   * @param {String} shInvoice
   * @param {String[]} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  declineExpediteInvoice(shInvoice, opt_includes = []) {
    const options = {};
    this.shData.addIncludesToOptions(options, []);

    return this.get('api').post(
      `sh-invoices/${shInvoice.get('id')}/decline-expedite`,
      options
    ).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.get('store').peekRecord('shInvoice', response.data.id);
    });
  },

  /**
   * Batch a set of invoices
   *
   * @param {ShInvoice[]} shInvoicesToBatch
   * @return {Ember.RSVP.Promise}     Returns newly batched ShInvoice
   */
  batchInvoices(shInvoicesToBatch) {
    return new Promise((resolve, reject) => {
      const options = {
        jsonData: {
          'sh-invoice-ids' : shInvoicesToBatch.map(shInvoice => shInvoice.get('id'))
        }
      };

      this.shData.addIncludesToOptions(options, []);

      this.get('api').post(`sh-invoices/batch`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let shInvoice = this.get('store').peekRecord('shInvoice', response.data.id);
        resolve(shInvoice);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error batching the invoices.  Please refresh and try again.',
          'batchInvoices -- error batching invoices : ' + shInvoicesToBatch.map(shInvoice => shInvoice.get('id')),
        );
        reject(e);
      });
    });
  },

  /**
   * @param {String} invoiceId
   * @param {String[]} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  loadShInvoice(invoiceId, opt_includes = []) {
    const options = {};
    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').request(`sh-invoices/${invoiceId}`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let shInvoice = this.get('store').peekRecord('shInvoice', response.data.id);
        resolve(shInvoice);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error loading the invoice.  Please refresh and try again.',
          'loadShInvoice -- error loading invoice id : ' + invoiceId,
        );
        reject(e);
      });
    });
  },

  /**
   * Load sh invoices from the server
   *
   * @param {Number=} opt_page
   * @param {Number=} opt_count
   * @param {Object=} opt_filter
   * @param {String[]=} opt_includes
   * @param {String=} opt_sort
   * @param {Boolean=} opt_isSkipCache
   * @return {Ember.RSVP.Promise}
   */
  loadShInvoices(
    opt_page = 1,
    opt_count = 10,
    opt_filter = {},
    opt_includes = [],
    opt_sort = '',
    opt_isSkipCache = false,
    opt_includeMetaCounts = false,
  ) {
    if (!opt_isSkipCache) {
      let responseObjectFromCache = this.appCache.getCachedModelArrayForRequest(this, 'loadShInvoices', arguments);
      if (responseObjectFromCache) {
        return Promise.resolve(responseObjectFromCache);
      }
    }

    const options = {
      params: {
        page: opt_page,
        count: opt_count,
        filter: opt_filter
      }
    };

    if (opt_sort) {
      options.params['sort'] = opt_sort;
    }

    if (opt_includeMetaCounts) {
      options.params['meta_counts'] = true;
    }

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').request(`sh-invoices/`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let shInvoices = this.shData.getModelArrayFromResponse(response, 'shInvoice'),
          responseObject = {
            shInvoices: shInvoices,
            pagination: this.shData.getPaginationModelFromResponse(response),
            countMeta: opt_includeMetaCounts ? getCountMetaFromResponse(response) : null,
          };

        if (!opt_isSkipCache) {
          this.appCache.setCachedModelArrayForRequest(
            this,
            'loadShInvoices',
            arguments,
            responseObject);
        }

        resolve(responseObject);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error finding these invoices. Please refresh and try again.',
          'loadShInvoices -- error loading invoices: ',
        );
        reject(e);
      });
    });
  },

  /**
   * Process an ShInvoice
   *
   * @param {ShInvoice} shInvoice
   * @return {Ember.RSVP.Promise}
   */
  processShInvoice(shInvoice) {
    return this.get('api').post(`sh-invoices/${shInvoice.get('id')}/process`);
  },

  /**
   * Remove a child invoice from its parent batch
   *
   * @param childShInvoice
   * @return {*}
   */
  removeFromBatch(childShInvoice) {
    return this.get('api').put(`sh-invoices/${childShInvoice.get('id')}/remove-from-batch`);
  },

  /**
   * Send an invoice receipt to a user.
   *
   * @param {ShInvoice} shInvoice
   * @return {Ember.RSVP.Promise}
   */
  sendInvoice(shInvoice) {
    const options = {};
    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').put(`sh-invoices/${shInvoice.get('id')}/send`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error sending the invoice.  Please refresh and try again.',
          'sendInvoice -- error sending invoice id : ' + shInvoice.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Unbatch this shInvoice. It must be a batched invoice.
   *
   * @param {ShInvoice} shInvoice
   * @return {Ember.RSVP.Promise}
   */
  unBatchInvoice(shInvoice) {
    return new Promise((resolve, reject) => {
      const options = {};
      this.shData.addIncludesToOptions(options, []);

      this.get('api').put(
        `sh-invoices/${shInvoice.get('id')}/unbatch`,
        options
      ).then(response => {
        this.shData.pushResponseToStore(response);
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error un-batching the invoice.  Please refresh and try again.',
          'unBatchInvoice -- error unBatching invoice : ' + shInvoice.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Updates the FeeStructure for a given ShInvoice
   *
   * @param {DS.Model} shInvoice
   * @param {Object} feeStructureJSON
   * @returns {Promise} API call to update the application FeeStructure
   */
  updateFeeStructure(shInvoice, feeStructureJSON) {
    const shInvoiceId = shInvoice.get('id');
    const endpoint = `sh-invoices/${shInvoiceId}/fee-structure`;

    const options = {
      jsonData: feeStructureJSON
    };

    return this.get('api').put(endpoint, options);
  },

  /**
   * Updates the revision number of an invoice
   *
   * @param {ShInvoice} shInvoice
   * @param {Number} revision
   * @return {*}
   */
  updateRevision(shInvoice, revision) {
    return this.get('api').put(
      `sh-invoices/${shInvoice.get('id')}/update-revision`, {
        jsonData: {
          revision
        }
      }
    );
  },
});
